import {
  parseEntityReferenceTokens,
  type LlmChatEntityReferenceDto,
} from '@paladin-monorepo/common';

/** Matches the attachment tokens the transcript has always understood. */
const ATTACHMENT_TOKEN =
  /(\[\[attachment:([^\]]+)\]\]|\bIMG(\d+)\b|\b(chatatt_[a-zA-Z0-9_-]+)\b)/g;

export type Segment =
  | { type: 'text'; value: string }
  | { type: 'attachment'; attachmentId?: string; index?: number; raw: string }
  | { type: 'reference'; reference: LlmChatEntityReferenceDto; raw: string };

export function parseAttachmentSegments(content: string): Segment[] {
  const segments: Segment[] = [];
  let lastIndex = 0;

  for (const match of content.matchAll(ATTACHMENT_TOKEN)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      segments.push({ type: 'text', value: content.slice(lastIndex, start) });
    }

    if (match[2]) {
      segments.push({ type: 'attachment', attachmentId: match[2], raw: match[0] });
    } else if (match[3]) {
      segments.push({ type: 'attachment', index: Number(match[3]), raw: match[0] });
    } else if (match[4]) {
      segments.push({ type: 'attachment', attachmentId: match[4], raw: match[0] });
    }

    lastIndex = start + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: 'text', value: content }];
}

/**
 * Splits message content into text, attachment and `@` reference segments.
 *
 * Reference tokens are taken out first, then each remaining run goes through the attachment
 * tokenizer - so neither regex has to know about the other.
 *
 * An entry present with `available: false` means the record was looked up and is gone, and
 * renders as struck through. A token with *no* entry means nobody told us either way, which
 * is not the same thing: it renders as a plain unlinked chip, so a caller that forgets to
 * pass `references` cannot make every record look deleted.
 */
export function parseSegments(
  content: string,
  references: LlmChatEntityReferenceDto[] = [],
): Segment[] {
  const tokens = parseEntityReferenceTokens(content);
  if (tokens.length === 0) return parseAttachmentSegments(content);

  const segments: Segment[] = [];
  let cursor = 0;

  for (const token of tokens) {
    if (token.start > cursor) {
      segments.push(...parseAttachmentSegments(content.slice(cursor, token.start)));
    }

    const resolved = references.find(
      (item) => item.type === token.type && item.id === token.id,
    );
    segments.push({
      type: 'reference',
      reference: resolved ?? {
        type: token.type,
        id: token.id,
        name: token.name,
        // Unknown, not deleted - see the note above.
        available: true,
        url: null,
      },
      raw: token.raw,
    });
    cursor = token.end;
  }

  if (cursor < content.length) {
    segments.push(...parseAttachmentSegments(content.slice(cursor)));
  }

  return segments;
}
